import { Fragment, type KeyboardEvent, type ReactNode } from 'react';
import { CheckIcon, ChevronLeftIcon, TagIcon, XIcon } from 'lucide-react';
import { formatCopy } from '@/i18n/ns';
import type { PlanOption, PlanTiming } from './api';
import { MAJOR_TICKS, tickLabel } from './plan-math';
import { discountText, type BillingCopy, type planStepView, type timingView } from './plan-view';

type StepView = ReturnType<typeof planStepView>;
type TimingView = ReturnType<typeof timingView>;

/** Fill `{name}` placeholders, rendering the named values in bold. */
export function rich(
  template: string,
  values: Record<string, string>,
  bold: readonly string[] = [],
): ReactNode {
  return template.split(/(\{[^}]+\})/).map((part, index) => {
    const key = part.startsWith('{') && part.endsWith('}') ? part.slice(1, -1) : null;
    if (!key || !Object.hasOwn(values, key)) return <Fragment key={index}>{part}</Fragment>;
    return bold.includes(key) ? (
      <strong key={index}>{values[key]}</strong>
    ) : (
      <Fragment key={index}>{values[key]}</Fragment>
    );
  });
}

export function DialogHead({
  title,
  sub,
  copy,
  onClose,
  onBack,
}: {
  title: string;
  sub?: string;
  copy: BillingCopy;
  onClose: () => void;
  onBack?: () => void;
}) {
  return (
    <div className="cp-head">
      <div>
        {onBack && (
          <button type="button" className="cp-back" onClick={onBack}>
            <ChevronLeftIcon size={14} aria-hidden="true" />
            {copy.back}
          </button>
        )}
        <h2 id="cp-title" className="cp-title">
          {title}
        </h2>
        {sub && <p className="cp-sub">{sub}</p>}
      </div>
      <button type="button" className="cp-close" aria-label={copy.close} onClick={onClose}>
        <XIcon size={18} aria-hidden="true" />
      </button>
    </div>
  );
}

const tickLeft = (index: number, count: number) =>
  `calc(13px + (100% - 26px) * ${(count > 1 ? index / (count - 1) : 0).toFixed(4)})`;

export function PlanSlider({
  plans,
  index,
  current,
  view,
  copy,
  locale,
  onChange,
}: {
  plans: PlanOption[];
  index: number;
  current: number;
  view: StepView;
  copy: BillingCopy;
  locale: string;
  onChange: (index: number) => void;
}) {
  const last = plans.length - 1;
  const fraction = last > 0 ? index / last : 0;
  return (
    <>
      <div className="cp-slider">
        <div className="cp-track">
          <div className="cp-fill" style={{ width: `${(fraction * 100).toFixed(3)}%` }} />
          {plans.map((plan, step) => (
            <span
              key={plan.id}
              className="cp-detent"
              data-filled={step <= index || undefined}
              style={{ left: `${((last > 0 ? step / last : 0) * 100).toFixed(3)}%` }}
            />
          ))}
        </div>
        {current >= 0 && (
          <span className="cp-pin" style={{ left: tickLeft(current, plans.length) }}>
            {copy.currentPin}
          </span>
        )}
        <input
          className="cp-range"
          type="range"
          min={0}
          max={last}
          step={1}
          value={index}
          aria-label={copy.sliderLabel}
          aria-valuetext={view.sliderValue}
          onChange={(event) => onChange(Number(event.currentTarget.value))}
          onInput={(event) => onChange(Number(event.currentTarget.value))}
        />
      </div>
      <div className="cp-ticks" aria-hidden="true">
        {plans.map((plan, step) => (
          <span
            key={plan.id}
            className="cp-tick"
            data-on={step === index || undefined}
            data-minor={!MAJOR_TICKS.has(step) || undefined}
            style={{ left: tickLeft(step, plans.length) }}
          >
            {tickLabel(plan.credits, locale)}
          </span>
        ))}
      </div>
      <div className="cp-ends">
        <span>{view.endLow}</span>
        <span>{view.endHigh}</span>
      </div>
    </>
  );
}

export type CodeRefusal = 'not_found' | 'already_used' | 'not_percent';

/** The server's refusal code for a checked code, as one of the reasons the field explains. */
export function codeRefusal(code: string | null | undefined): CodeRefusal {
  if (code === 'code_already_used') return 'already_used';
  if (code === 'code_not_percent') return 'not_percent';
  return 'not_found';
}

const REFUSAL_TEXT = {
  not_found: 'codeNotFound',
  already_used: 'codeAlreadyUsed',
  not_percent: 'codeNotPercent',
} as const satisfies Record<CodeRefusal, keyof BillingCopy>;

export interface CodeFieldState {
  editing: boolean;
  draft: string;
  error: boolean;
  /** Why the code was refused: unknown, already used (one use per company) or an amount-off code. */
  refusal: CodeRefusal;
  busy: boolean;
  open: () => void;
  cancel: () => void;
  setDraft: (value: string) => void;
  apply: () => void;
  remove: () => void;
}

export function CodeField({
  code,
  view,
  copy,
  locale,
}: {
  code: CodeFieldState;
  view: StepView;
  copy: BillingCopy;
  locale: string;
}) {
  const discount = view.discount;
  if (discount)
    return (
      <div className="cp-code">
        <div className="cp-chip" data-testid="plan-code-chip">
          <TagIcon size={15} aria-hidden="true" />
          {discount.code && <span className="cp-chip-name">{discount.code}</span>}
          <span className="cp-chip-desc">
            {discountText(discount, copy, locale, !discount.fresh && !view.isCurrent)}
          </span>
          {discount.fresh && (
            <button
              type="button"
              className="cp-chip-x"
              aria-label={copy.codeRemove}
              onClick={code.remove}
            >
              <XIcon size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  if (!code.editing)
    return (
      <div className="cp-code">
        <button type="button" className="cp-code-add" onClick={code.open}>
          <TagIcon size={15} aria-hidden="true" />
          {copy.codeAdd}
        </button>
      </div>
    );
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      code.apply();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      code.cancel();
    }
  };
  return (
    <div className="cp-code">
      <div className="cp-code-form">
        <span className="cp-input-wrap" data-invalid={code.error || undefined}>
          <input
            className="cp-input"
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder={copy.codePlaceholder}
            aria-label={copy.codePlaceholder}
            aria-invalid={code.error}
            value={code.draft}
            autoFocus
            onChange={(event) => code.setDraft(event.currentTarget.value)}
            onInput={(event) => code.setDraft(event.currentTarget.value)}
            onKeyDown={onKeyDown}
          />
        </span>
        <button
          type="button"
          className="cp-btn"
          disabled={code.busy || !code.draft.trim()}
          onClick={code.apply}
        >
          {code.busy ? copy.codeChecking : copy.codeApply}
        </button>
      </div>
      {code.error && (
        <p className="cp-error" role="alert">
          {copy[REFUSAL_TEXT[code.refusal]]}
        </p>
      )}
    </div>
  );
}

export function SidePanel({
  view,
  copy,
  children,
}: {
  view: StepView;
  copy: BillingCopy;
  children: ReactNode;
}) {
  const on = Boolean(view.discount);
  return (
    <aside className="cp-side" aria-label={copy.priceLabel}>
      <div className="cp-spec">
        <span className="cp-spec-label">{copy.priceLabel}</span>
        <span className="cp-spec-value">
          {on && <s className="cp-was">{view.listPrice}</s>}
          {view.price}
          <small>{view.priceNote}</small>
        </span>
      </div>
      <div className="cp-spec">
        <span className="cp-spec-label">{copy.perCreditLabel}</span>
        <span className="cp-spec-value">{view.perCredit}</span>
      </div>
      <div className="cp-spec" data-discount>
        <span className="cp-spec-label">{copy.volumeLabel}</span>
        <div className="cp-discount">
          <span
            className="cp-discount-num"
            data-none={view.plan.volumeDiscountPercent > 0 ? undefined : true}
          >
            {view.volume}
          </span>
          <span className="cp-discount-note">{view.volumeNote}</span>
        </div>
      </div>
      {view.mode === 'reverse' ? (
        <div className="cp-spec">
          <span className="cp-spec-label">{copy.vatReverseLabel}</span>
          <span className="cp-spec-value">{copy.vatReverseValue}</span>
        </div>
      ) : (
        <div className="cp-spec">
          <span className="cp-spec-label">{copy.vatLabel}</span>
          <span className="cp-spec-value">
            {on && <s className="cp-was">{view.listVat}</s>}
            {view.vat}
            <small>{copy.perMonth}</small>
          </span>
        </div>
      )}
      {children}
    </aside>
  );
}

function Choice({
  checked,
  title,
  rows,
  onPick,
}: {
  checked: boolean;
  title: string;
  rows: Array<[string, ReactNode]>;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className="cp-choice"
      role="radio"
      aria-checked={checked}
      onClick={onPick}
    >
      <span className="cp-choice-head">
        <span className="cp-choice-title">{title}</span>
        <span className="cp-radio">
          {checked && <CheckIcon size={12} strokeWidth={3} aria-hidden="true" />}
        </span>
      </span>
      {rows.map(([label, text]) => (
        <span className="cp-when" key={label}>
          <span className="cp-when-label">{label}</span>
          <span className="cp-when-text">{text}</span>
        </span>
      ))}
    </button>
  );
}

export function TimingChoices({
  view,
  timing,
  copy,
  onPick,
}: {
  view: TimingView;
  timing: PlanTiming;
  copy: BillingCopy;
  onPick: (timing: PlanTiming) => void;
}) {
  const bold = ['price', 'credits', 'fullPrice'];
  const value = view.value;
  const renewalDate = view.renewalLabel;
  return (
    <div className="cp-choices" role="radiogroup" aria-label={copy.timingGroup}>
      <Choice
        checked={timing === 'now'}
        title={copy.nowTitle}
        onPick={() => onPick('now')}
        rows={[
          [copy.today, rich(view.nowToday, value, bold)],
          [copy.fromThenOn, rich(view.nowThen, { ...value, date: view.nowThenDate }, bold)],
        ]}
      />
      {renewalDate && (
        <Choice
          checked={timing === 'renewal'}
          title={formatCopy(copy.renewalTitle, { date: renewalDate })}
          onPick={() => onPick('renewal')}
          rows={[
            [copy.today, rich(view.renewalToday, value)],
            [
              formatCopy(copy.onDate, { date: renewalDate }),
              rich(copy.renewalThen, { ...value, codeNote: view.renewalCodeNote }, bold),
            ],
          ]}
        />
      )}
    </div>
  );
}
